import { type FormEvent, useEffect, useId, useRef } from 'react';
import { useToast } from '@lms/ui';
import {
  FORUM_MAX_BODY_LENGTH,
  FORUM_MAX_TITLE_LENGTH,
  FORUM_MIN_BODY_LENGTH,
  FORUM_MIN_TITLE_LENGTH,
  forumBodyError,
  forumTitleError,
} from '@lms/shared-types';

interface CourseForumNewThreadModalProps {
  open: boolean;
  saving?: boolean;
  submitError?: string | null;
  title?: string;
  onClose: () => void;
  onSubmit: (payload: { title: string; body: string }) => void;
}

export function CourseForumNewThreadModal({
  open,
  saving = false,
  submitError = null,
  title = 'Nuevo hilo de debate',
  onClose,
  onSubmit,
}: CourseForumNewThreadModalProps) {
  const titleId = useId();
  const formId = useId();
  const titleRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    titleRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!submitError) return;
    toast({ title: submitError, variant: 'danger' });
  }, [submitError, toast]);

  if (!open) return null;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const nextTitle = (form.elements.namedItem('title') as HTMLInputElement).value;
    const nextBody = (form.elements.namedItem('body') as HTMLTextAreaElement).value;
    const titleError = forumTitleError(nextTitle);
    const bodyError = forumBodyError(nextBody);
    if (titleError) toast({ title: titleError, variant: 'warning' });
    if (bodyError) toast({ title: bodyError, variant: 'warning' });
    if (titleError || bodyError) return;
    onSubmit({ title: nextTitle.trim(), body: nextBody.trim() });
  }

  return (
    <div
      className="lms-course-forum-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="lms-course-forum-modal__panel">
        <div className="lms-course-forum-modal__head">
          <h3 id={titleId} className="lms-course-forum-modal__title">
            {title}
          </h3>
          <button
            type="button"
            className="lms-course-forum-modal__close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              close
            </span>
          </button>
        </div>

        <form id={formId} className="lms-course-forum-modal__body" onSubmit={handleSubmit} noValidate>
          <div className="lms-course-forum-modal__field">
            <label htmlFor={`${formId}-title`} className="lms-course-forum-modal__label">
              Título de la publicación
            </label>
            <input
              ref={titleRef}
              id={`${formId}-title`}
              name="title"
              type="text"
              required
              minLength={FORUM_MIN_TITLE_LENGTH}
              maxLength={FORUM_MAX_TITLE_LENGTH}
              placeholder="Ej. Duda sobre la unidad 2..."
              className="lms-course-forum-modal__input"
            />
            <p className="lms-course-forum-modal__hint">Mínimo {FORUM_MIN_TITLE_LENGTH} caracteres.</p>
          </div>
          <div className="lms-course-forum-modal__field">
            <label htmlFor={`${formId}-body`} className="lms-course-forum-modal__label">
              Contenido
            </label>
            <textarea
              id={`${formId}-body`}
              name="body"
              required
              rows={4}
              minLength={FORUM_MIN_BODY_LENGTH}
              maxLength={FORUM_MAX_BODY_LENGTH}
              placeholder="Escribe tu consulta o aporte académico aquí..."
              className="lms-course-forum-modal__textarea"
            />
            <p className="lms-course-forum-modal__hint">Mínimo {FORUM_MIN_BODY_LENGTH} caracteres.</p>
          </div>
        </form>

        <div className="lms-course-forum-modal__footer">
          <button type="button" className="lms-course-forum-modal__btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="submit"
            form={formId}
            className="lms-course-forum-modal__btn-primary"
            disabled={saving}
          >
            {saving ? 'Publicando…' : 'Publicar hilo'}
          </button>
        </div>
      </div>
    </div>
  );
}
